import { Close, Send } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  ThemeProvider,
  useTheme,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { ModuleContext } from "../../context/ModuleContext";
import { InputTheme } from "../../themes/InputTheme";
import { AlternateAliasContext } from "../../context/AlternateAliasContext";

const WSU_RED = "#A60F2D";

const labelCellSx = {
  fontWeight: 600,
  color: "#444",
  width: "35%",
  fontSize: "0.9rem",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    "&:hover fieldset": { borderColor: WSU_RED },
    "&.Mui-focused fieldset": { borderColor: WSU_RED },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: WSU_RED },
};

export const UpdateAlias = () => {
  const { authState } = useOktaAuth();
  const { aliasModule, setAliasModule } = useContext(AlternateAliasContext);
  const { setModule } = useContext(ModuleContext);
  const outerTheme = useTheme();

  const [state, setState] = useState({
    isLoaded: true,
    error: null,
    comment: "",
    manager: aliasModule.data["Manager"] || "",
    targetAddress: aliasModule.data["Target Address"] || "",
    department: aliasModule.data["Department"] || "",
    message: null,
  });

  const isFormValid = useMemo(
      () => state.manager.trim() && state.targetAddress.trim() && state.comment.trim(),
      [state]
  );

  const updateAlias = async () => {
    setState((prev) => ({ ...prev, isLoaded: false, error: null }));

    try {
      const response = await fetch(
          `${process.env.REACT_APP_AWS_API_GATEWAY_ENDPOINT}/alias/update`,
          {
            method: "POST",
            headers: {
              Authorization: authState.accessToken.accessToken,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              targetAddress: state.targetAddress,
              department: state.department,
              manager: state.manager,
              callerId: authState.accessToken.claims.sub,
              comment: state.comment,
              dn: aliasModule.dn,
              userPrincipalName: aliasModule.upn,
            }),
          }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error?.message || "Update failed");
      }

      const result = await response.json();

      const updatedData = {
        ...aliasModule.data,
        Department: result["Department"],
        Manager: result["Manager"],
        "Target Address": result["Target Address"],
      };

      setAliasModule((prev) => ({ ...prev, data: updatedData, isLoaded: true }));
      setState((prev) => ({ ...prev, isLoaded: true, message: "Update successful" }));
    } catch (error) {
      setState((prev) => ({ ...prev, isLoaded: true, error }));
    }
  };

  if (!state.isLoaded) {
    return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
          <CircularProgress style={{ color: WSU_RED }} size={80} thickness={5} />
        </Box>
    );
  }

  if (state.message) {
    return (
        <Box sx={{ width: "100%", position: "relative", left: "50%", right: "50%", ml: "-50vw", mr: "-50vw", display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 1200, borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Update Complete
              </Typography>
            </Box>
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {state.message}
              </Typography>
            </Box>
            <Box sx={{ px: 3, pb: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                  variant="outlined"
                  endIcon={<Close />}
                  onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                  sx={{ borderColor: "#ccc", color: "#666", "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" } }}
              >
                Close
              </Button>
            </Box>
          </TableContainer>
        </Box>
    );
  }

  return (
      <ThemeProvider theme={InputTheme(outerTheme)}>
        <Box sx={{ width: "100%", position: "relative", left: "50%", right: "50%", ml: "-50vw", mr: "-50vw", display: "flex", justifyContent: "center", px: 2, py: 4 }}>
          <TableContainer component={Paper} elevation={2} sx={{ maxWidth: 1200, borderRadius: 2, overflow: "hidden" }}>

            {/* Error Banner */}
            {state.error && (
                <Box sx={{ backgroundColor: WSU_RED, color: "white", px: 3, py: 2 }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Update failed
                  </Typography>
                  <Typography variant="body2">{state.error.message}</Typography>
                </Box>
            )}

            {/* ── Section: Alias Details ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Alias Details
              </Typography>
            </Box>

            <Table sx={{ minWidth: 500 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Alias</TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {aliasModule.upn}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Organization ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Organization
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Manager *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.manager}
                        onChange={(e) => setState((prev) => ({ ...prev, manager: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell sx={labelCellSx}>Department</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.department}
                        onChange={(e) => setState((prev) => ({ ...prev, department: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Mail ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Mail
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Target Address *</TableCell>
                  <TableCell>
                    <TextField
                        fullWidth
                        size="small"
                        value={state.targetAddress}
                        onChange={(e) => setState((prev) => ({ ...prev, targetAddress: e.target.value }))}
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ mx: 3 }} />

            {/* ── Section: Justification ── */}
            <Box sx={{ px: 3, pt: 3, pb: 1 }}>
              <Typography variant="overline" sx={{ color: WSU_RED, fontWeight: 700, letterSpacing: 1.5 }}>
                Justification
              </Typography>
            </Box>

            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={labelCellSx}>Comment *</TableCell>
                  <TableCell>
                    <TextField
                        required
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        value={state.comment}
                        onChange={(e) => setState((prev) => ({ ...prev, comment: e.target.value }))}
                        placeholder="Provide a reason for updating this alias"
                        sx={fieldSx}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            {/* ── Submit / Cancel ── */}
            <Box sx={{ px: 3, pt: 3, pb: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}>
              <Button
                  variant="outlined"
                  endIcon={<Close />}
                  onClick={() => setModule((prev) => ({ ...prev, isVisible: false }))}
                  sx={{
                    borderColor: "#ccc",
                    color: "#666",
                    "&:hover": { borderColor: "#aaa", backgroundColor: "#f9f9f9" },
                  }}
              >
                Cancel
              </Button>
              <Button
                  variant="contained"
                  endIcon={<Send />}
                  onClick={updateAlias}
                  disabled={!isFormValid}
                  sx={{
                    backgroundColor: WSU_RED,
                    color: "white",
                    px: 4,
                    py: 1.25,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    "&:hover": { backgroundColor: "#8a0c25" },
                    "&.Mui-disabled": { backgroundColor: "#ddd", color: "#aaa" },
                  }}
              >
                Update Alias
              </Button>
            </Box>

          </TableContainer>
        </Box>
      </ThemeProvider>
  );
};